import { type MouseEvent, type PointerEvent, useMemo, useRef, useState } from 'react';
import { SIMULATOR_YEARS } from 'used-vehicle-engine';
import { pct } from './format';

export interface ChartPoint {
  n: number;
  v: number;
}

export interface ChartSeries {
  key: string;
  name: string;
  color: string;
  width: number;
  dash?: string;
  /** Draw a dot at this year (the car's age). */
  markAt?: number;
  pts: ChartPoint[];
}

export interface TooltipRow {
  label: string;
  value: string;
  note?: string;
}

export interface LineChartProps {
  series: ChartSeries[];
  /** `0` draws a "New" origin before Year 1; `1` starts at Year 1. */
  x0: 0 | 1;
  step: number;
  yMin: number;
  yMax: number;
  label: string;
  rows: (n: number) => TooltipRow[];
  selected: string;
  onPick: (key: string | null) => void;
}

const W = 600;
const H = 330;
const M = { l: 44, r: 150, t: 14, b: 34 };
const PW = W - M.l - M.r;
const PH = H - M.t - M.b;

interface PlacedLabel {
  key: string;
  x: number;
  y: number;
  ly: number;
}

/** Pushes end labels apart so none overlap, top to bottom, then back up if the last one ran off the plot. */
const spread = (labels: PlacedLabel[], top: number, bottom: number, gap: number): PlacedLabel[] => {
  const sorted = [...labels].sort((a, b) => a.y - b.y);

  sorted.forEach((label, index) => {
    label.ly = Math.max(label.y, index ? sorted[index - 1]!.ly + gap : top);
  });

  const over = sorted.length ? sorted[sorted.length - 1]!.ly - bottom : 0;

  if (over > 0) {
    for (let index = sorted.length - 1; index >= 0; index -= 1) {
      sorted[index]!.ly = Math.min(sorted[index]!.ly, index === sorted.length - 1 ? bottom : sorted[index + 1]!.ly - gap);
    }
  }

  return sorted;
};

/**
 * The simulator's SVG line chart: a fixed 600×330 viewBox, labels at the line ends, a crosshair with a
 * tooltip under the pointer and click-to-highlight on a line. A port of the one-file HTML version.
 */
export const LineChart = ({ series, x0, step, yMin, yMax, label, rows, selected, onPick }: LineChartProps) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ n: number; left: number; top: number } | null>(null);

  const scale = useMemo(() => {
    let lo = yMin;
    let hi = yMax;

    for (const s of series) {
      for (const p of s.pts) {
        lo = Math.min(lo, p.v);
        hi = Math.max(hi, p.v);
      }
    }

    lo = Math.max(0, Math.floor(lo / step + 1e-9) * step);
    hi = Math.ceil(hi / step - 1e-9) * step;

    if (hi <= lo) {
      hi = lo + step;
    }

    const X = (n: number): number => (x0 === 0 ? M.l + (PW * n) / SIMULATOR_YEARS : M.l + 14 + ((PW - 28) * (n - 1)) / (SIMULATOR_YEARS - 1));
    const Y = (v: number): number => M.t + (PH * (hi - v)) / (hi - lo);
    const ticks: number[] = [];

    for (let t = lo; t <= hi + 1e-9; t += step) {
      ticks.push(t);
    }

    return { lo, hi, X, Y, ticks };
  }, [series, step, x0, yMin, yMax]);

  const labels = useMemo(() => {
    const placed: PlacedLabel[] = [];

    for (const s of series) {
      const last = s.pts[s.pts.length - 1];

      if (last) {
        placed.push({ key: s.key, x: scale.X(last.n), y: scale.Y(last.v), ly: 0 });
      }
    }

    return spread(placed, M.t + 6, M.t + PH - 2, 13);
  }, [series, scale]);

  const nearestYear = (clientX: number): number => {
    const box = svgRef.current?.getBoundingClientRect();

    if (!box) {
      return x0;
    }

    const sx = ((clientX - box.left) / box.width) * W;
    let best = x0;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (let n = x0; n <= SIMULATOR_YEARS; n += 1) {
      const distance = Math.abs(scale.X(n) - sx);

      if (distance < bestDistance) {
        bestDistance = distance;
        best = n;
      }
    }

    return best;
  };

  const onMove = (event: PointerEvent<SVGRectElement>): void => {
    const host = hostRef.current?.getBoundingClientRect();

    if (!host) {
      return;
    }

    const n = nearestYear(event.clientX);
    const px = event.clientX - host.left;
    const py = event.clientY - host.top;
    const tipWidth = tipRef.current?.offsetWidth ?? 200;
    const tipHeight = tipRef.current?.offsetHeight ?? 80;

    setHover({
      n,
      left: Math.max(0, Math.min(host.width - tipWidth, px + (px > host.width / 2 ? -tipWidth - 14 : 14))),
      top: Math.max(0, py - tipHeight - 10),
    });
  };

  const onClick = (event: MouseEvent<SVGSVGElement>): void => {
    const box = svgRef.current?.getBoundingClientRect();

    if (!box) {
      return;
    }

    const sx = ((event.clientX - box.left) / box.width) * W;
    const sy = ((event.clientY - box.top) / box.height) * H;
    let best: string | null = null;
    let bestDistance = 14;

    for (const s of series) {
      for (let index = 0; index < s.pts.length - 1; index += 1) {
        const a = s.pts[index]!;
        const b = s.pts[index + 1]!;
        const xa = scale.X(a.n);
        const xb = scale.X(b.n);

        if (sx < xa || sx > xb) {
          continue;
        }

        const y = scale.Y(a.v) + ((sx - xa) / (xb - xa || 1)) * (scale.Y(b.v) - scale.Y(a.v));
        const distance = Math.abs(y - sy);

        if (distance < bestDistance) {
          bestDistance = distance;
          best = s.key;
        }
      }
    }

    if (!best) {
      const group = (event.target as Element).closest('.series');

      best = group?.getAttribute('data-key') ?? null;
    }

    onPick(best);
  };

  return (
    <div
      ref={hostRef}
      className={`plot${selected ? ' has-selection' : ''}`}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={label}
        onClick={onClick}
      >
        {scale.ticks.map((t) => (
          <g key={t}>
            <line
              className="grid"
              x1={M.l}
              x2={M.l + PW}
              y1={scale.Y(t)}
              y2={scale.Y(t)}
            />
            <text
              x={M.l - 8}
              y={scale.Y(t) + 4}
              textAnchor="end"
            >
              {pct(t)}
            </text>
          </g>
        ))}
        <line
          className="axis"
          x1={M.l}
          x2={M.l + PW}
          y1={scale.Y(scale.lo)}
          y2={scale.Y(scale.lo)}
        />
        {Array.from({ length: SIMULATOR_YEARS - x0 + 1 }, (_, index) => x0 + index).map((n) => (
          <text
            key={n}
            x={scale.X(n)}
            y={H - 12}
            textAnchor="middle"
          >
            {n === 0 ? 'New' : `Year ${n}`}
          </text>
        ))}
        {series.map((s) => {
          const placed = labels.find((l) => l.key === s.key);
          const d = s.pts.map((p, index) => `${index ? 'L' : 'M'}${scale.X(p.n).toFixed(1)} ${scale.Y(p.v).toFixed(1)}`).join(' ');
          const lx = M.l + PW + 10;

          return (
            <g
              key={s.key}
              className={`series${selected === s.key ? ' selected' : ''}`}
              data-key={s.key}
            >
              {s.pts.length > 0 && (
                <path
                  d={d}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={s.width}
                  strokeDasharray={s.dash}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              )}
              {s.markAt !== undefined &&
                s.pts
                  .filter((p) => p.n === s.markAt)
                  .map((p) => (
                    <circle
                      key={p.n}
                      cx={scale.X(p.n)}
                      cy={scale.Y(p.v)}
                      r={5.2}
                      fill={s.color}
                      stroke="var(--paper)"
                      strokeWidth={1.75}
                    />
                  ))}
              {placed && (
                <>
                  {(placed.x < M.l + PW - 1 || Math.abs(placed.ly - placed.y) > 3) && (
                    <line
                      x1={placed.x + 6}
                      y1={placed.y}
                      x2={lx - 3}
                      y2={placed.ly}
                      stroke="var(--rule)"
                      strokeWidth={1}
                    />
                  )}
                  <line
                    x1={lx}
                    x2={lx + 12}
                    y1={placed.ly}
                    y2={placed.ly}
                    stroke={s.color}
                    strokeWidth={s.width}
                    strokeDasharray={s.dash}
                  />
                  <text
                    className="lab"
                    x={lx + 16}
                    y={placed.ly + 4}
                  >
                    {s.name}
                  </text>
                </>
              )}
            </g>
          );
        })}
        {hover && (
          <line
            className="cross"
            x1={scale.X(hover.n)}
            x2={scale.X(hover.n)}
            y1={M.t}
            y2={M.t + PH}
          />
        )}
        <rect
          className="hit"
          x={M.l}
          y={M.t}
          width={PW}
          height={PH}
          fill="transparent"
          onPointerMove={onMove}
          onPointerDown={onMove}
          onPointerLeave={() => setHover(null)}
        />
      </svg>
      <div
        ref={tipRef}
        className="tip"
        hidden={!hover}
        style={hover ? { left: hover.left, top: hover.top } : undefined}
      >
        {hover && (
          <>
            <b>{hover.n === 0 ? 'New' : `Year ${hover.n}`}</b>
            {rows(hover.n).map((row) => (
              <div
                key={row.label}
                className="row"
              >
                <span>{row.label}</span>
                <span>
                  {row.value}
                  {row.note && <em> · {row.note}</em>}
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export interface LegendItem {
  key: string;
  name: string;
  color: string;
  width: number;
  dash?: string;
  note?: string;
}

export interface LegendProps {
  items: LegendItem[];
  selected: string;
  onPick: (key: string | null) => void;
}

export const Legend = ({ items, selected, onPick }: LegendProps) => (
  <div className={`legend${selected ? ' has-selection' : ''}`}>
    {items.map((item) => (
      <button
        key={item.key}
        type="button"
        className={`series-btn${selected === item.key ? ' selected' : ''}`}
        aria-pressed={selected === item.key}
        onClick={() => onPick(item.key)}
      >
        <svg
          width="26"
          height="12"
          viewBox="0 0 26 12"
        >
          <line
            x1="1"
            x2="25"
            y1="6"
            y2="6"
            stroke={item.color}
            strokeWidth={item.width}
            strokeDasharray={item.dash}
          />
        </svg>
        {item.name}
        {item.note && <span style={{ color: 'var(--ink-3)' }}>{item.note}</span>}
      </button>
    ))}
    <span className="hint">Select a line or its name to highlight it. Select it again to show all.</span>
  </div>
);
